import React, { useState, useEffect, useRef } from 'react';
import { useSpotifyPlayer } from '../contexts/SpotifyPlayerContext';

const PlaylistPlayer = ({ 
  currentTrack, 
  playlist,
  isPlaying,
  onPlayPause,
  onNext,
  onPrevious,
  onSeek,
  playbackMode,
  onExit,
  onBackToPlaylist
}) => {
  const spotifyPlayer = useSpotifyPlayer();
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [gradientMode, setGradientMode] = useState('on'); // 'off', 'on', 'sync' - default to ON
  const [beatIntensity, setBeatIntensity] = useState(0);
  const audioRef = useRef(null);
  const hasAutoPlayedRef = useRef(false);
  const lastPositionRef = useRef(0); // Track last position for resume
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const isSpotifyTrack = currentTrack?.source === 'spotify' && currentTrack?.spotify_uri;

  // Auto-initialize Spotify player if needed
  useEffect(() => {
    const initSpotifyPlayer = async () => {
      if (isSpotifyTrack && !spotifyPlayer.isReady && !spotifyPlayer.player) {
        const token = localStorage.getItem('spotify_access_token');
        if (token) {
          console.log('🔄 Auto-initializing Spotify player for playlist playback...');
          try {
            const success = await spotifyPlayer.connect(token);
            if (success) {
              console.log('✅ Spotify player initialized successfully!');
              console.log('⏳ Waiting for player to be ready...');
            } else {
              console.error('❌ Failed to initialize Spotify player');
            }
          } catch (error) {
            console.error('❌ Failed to initialize Spotify player:', error);
          }
        } else {
          console.warn('⚠️ No Spotify access token found. Please connect Spotify first.');
        }
      }
    };

    initSpotifyPlayer();
  }, [isSpotifyTrack, spotifyPlayer.isReady, spotifyPlayer.player]);

  // Handle Spotify track playback - only when player is ready
  useEffect(() => {
    const playSpotifyTrack = async () => {
      // Only proceed if we have a Spotify track
      if (!isSpotifyTrack) return;

      console.log('🎵 Spotify Track Detected:', currentTrack.title);
      console.log('   - Spotify URI:', currentTrack.spotify_uri);
      console.log('   - Player Ready:', spotifyPlayer.isReady);
      console.log('   - Device ID:', spotifyPlayer.deviceId);
      console.log('   - Should Play:', isPlaying);
      console.log('   - Last Position:', lastPositionRef.current);

      // Don't attempt any action if player is not ready yet
      if (!spotifyPlayer.isReady || !spotifyPlayer.deviceId) {
        console.log('⏳ Waiting for Spotify player to be ready...');
        return;
      }

      // Now player is ready, handle play/pause
      if (isPlaying) {
        console.log('▶️ Attempting to play Spotify track via SDK...');
        try {
          // Resume from last position if available, otherwise start from beginning
          const startPosition = lastPositionRef.current || 0;
          console.log(`   Starting from: ${startPosition}ms`);
          await spotifyPlayer.play(currentTrack.spotify_uri, startPosition);
          console.log('✅ Spotify playback started!');
        } catch (error) {
          console.error('❌ Error playing Spotify track:', error);
        }
      } else {
        console.log('⏸️ Pausing Spotify track');
        // Save current position before pausing
        if (spotifyPlayer.position !== undefined) {
          lastPositionRef.current = spotifyPlayer.position;
          console.log(`   Saved position: ${lastPositionRef.current}ms`);
        }
        await spotifyPlayer.pause();
      }
    };

    playSpotifyTrack();
  }, [currentTrack, isPlaying, isSpotifyTrack, spotifyPlayer.isReady, spotifyPlayer.deviceId]);

  // Update progress from Spotify player continuously
  useEffect(() => {
    if (isSpotifyTrack && spotifyPlayer.position !== undefined) {
      setProgress(spotifyPlayer.position / 1000); // Convert ms to seconds
      lastPositionRef.current = spotifyPlayer.position; // Keep last position updated
      
      if (currentTrack?.duration_ms) {
        setDuration(currentTrack.duration_ms / 1000);
      } else if (spotifyPlayer.currentTrack?.duration_ms) {
        setDuration(spotifyPlayer.currentTrack.duration_ms / 1000);
      }
    }
  }, [isSpotifyTrack, spotifyPlayer.position, spotifyPlayer.currentTrack, currentTrack]);

  // Reset position when track changes
  useEffect(() => {
    if (currentTrack) {
      lastPositionRef.current = 0;
      setProgress(0);
    }
  }, [currentTrack?.track_id]);

  // Auto-play when first loaded with isPlaying true (non-Spotify tracks)
  useEffect(() => {
    if (!isSpotifyTrack && currentTrack && isPlaying && !hasAutoPlayedRef.current) {
      hasAutoPlayedRef.current = true;
      if (currentTrack.preview_url && audioRef.current) {
        // Give a small delay for the audio element to be ready
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().catch(err => {
              console.error('Initial auto-play error:', err);
            });
          }
        }, 200);
      }
    }
    
    // Reset when track changes
    if (currentTrack) {
      hasAutoPlayedRef.current = false;
    }
  }, [currentTrack?.title, isPlaying, isSpotifyTrack]);

  // Load track src when track changes (not when isPlaying changes)
  useEffect(() => {
    if (!isSpotifyTrack && currentTrack) {
      console.log('🎵 Non-Spotify Track Detected:', currentTrack.title);
      console.log('   - Source:', currentTrack.source);
      console.log('   - Preview URL:', currentTrack.preview_url);
      console.log('   - Local ID:', currentTrack.local_id);
      
      // Handle local tracks - need to fetch from IndexedDB and create Object URL
      const handleLocalTrack = async () => {
        if (currentTrack.source === 'local' && currentTrack.local_id) {
          console.log('📁 Loading local track from IndexedDB:', currentTrack.local_id);
          try {
            const { getLocalTrack, createTrackURL } = await import('../utils/localTrackStorage');
            const localTrack = await getLocalTrack(currentTrack.local_id);
            console.log('📁 Retrieved local track:', localTrack);
            
            if (localTrack) {
              const objectURL = createTrackURL(localTrack);
              console.log('📁 Created object URL:', objectURL);
              
              if (objectURL && audioRef.current) {
                audioRef.current.src = objectURL;
                audioRef.current.volume = volume;
                audioRef.current.load();
              } else {
                console.error('❌ Failed to create object URL for local track');
              }
            } else {
              console.error('❌ Local track not found in IndexedDB for ID:', currentTrack.local_id);
            }
          } catch (error) {
            console.error('❌ Error loading local track:', error);
          }
        } else {
          console.warn('⚠️ Local track missing local_id:', currentTrack);
        }
      };
      
      // Handle Discovery Zone tracks with preview URLs
      if (currentTrack.preview_url && audioRef.current) {
        console.log('🔊 Setting up Discovery Zone preview track');
        audioRef.current.src = currentTrack.preview_url;
        audioRef.current.volume = volume;
      } else if (currentTrack.source === 'local') {
        // Handle local tracks
        handleLocalTrack();
      } else {
        // No preview URL available for non-Spotify track
        console.warn('⚠️ Track has no preview URL:', currentTrack.title);
        console.warn('   Track data:', currentTrack);
      }
    }
  }, [currentTrack, isSpotifyTrack]); // volume intentionally excluded to prevent re-loading track on volume change
  
  // Separate effect for play/pause control
  useEffect(() => {
    if (!isSpotifyTrack && audioRef.current && currentTrack) {
      if (isPlaying) {
        console.log('▶️ Play command received');
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.error('❌ Play error, retrying...', err);
            // Retry once after a delay
            setTimeout(() => {
              if (audioRef.current) {
                audioRef.current.play()
                  .then(() => console.log('✅ Retry successful!'))
                  .catch(e => console.error('❌ Retry play error:', e));
              }
            }, 100);
          });
        }
      } else {
        console.log('⏸️ Pause command received');
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isSpotifyTrack, currentTrack]);

  // Update progress bar for HTML5 audio
  useEffect(() => {
    if (!audioRef.current || isSpotifyTrack) return;

    const updateProgress = () => {
      // Safety check - audioRef might be null during unmount
      if (audioRef.current) {
        setProgress(audioRef.current.currentTime);
        setDuration(audioRef.current.duration || 0);
      }
    };

    const handleEnded = () => {
      console.log('🎵 Track ended, moving to next...');
      if (onNext) {
        onNext();
      }
    };

    audioRef.current.addEventListener('timeupdate', updateProgress);
    audioRef.current.addEventListener('loadedmetadata', updateProgress);
    audioRef.current.addEventListener('ended', handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('loadedmetadata', updateProgress);
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [isSpotifyTrack, onNext]);

  // Smooth progress update for Spotify (interpolate between state updates)
  useEffect(() => {
    if (!isSpotifyTrack || !isPlaying) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 0.1; // Add 100ms
        if (newProgress >= duration) {
          return duration;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isSpotifyTrack, isPlaying, duration]);

  // Beat detection for gradient sync mode
  useEffect(() => {
    if (gradientMode !== 'sync' || !audioRef.current || !isPlaying || isSpotifyTrack) return;

    // Initialize Web Audio API with CORS error handling
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048; // Higher resolution for precise bass frequency analysis
        analyserRef.current.smoothingTimeConstant = 0; // Zero smoothing for immediate response
        
        const source = audioContextRef.current.createMediaElementSource(audioRef.current);
        source.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
        
        console.log('✅ Audio context initialized for beat detection');
      } catch (error) {
        console.error('❌ Error initializing audio context (likely CORS issue):', error);
        console.warn('⚠️ Beat sync disabled - audio source does not allow Web Audio API access');
        // Fall back to gradient ON mode
        setGradientMode('on');
        alert('⚠️ Beat sync not available for this track (CORS restriction). Using gradient ON mode instead.');
        return;
      }
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    let animationFrameId;

    let lastPeakTime = 0;
    let peakHoldTime = 0;
    let lastIntensity = 0;
    
    const detectBeat = () => {
      try {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Focus ONLY on kick/bass frequencies (bins 0-6 for ~20-140Hz at 2048 FFT)
        const kickBassSum = dataArray.slice(0, 6).reduce((sum, val) => sum + val, 0);
        const kickBassAvg = kickBassSum / 6;
        
        // Calculate raw intensity from bass only
        const rawIntensity = kickBassAvg / 255;
        
        // Detect sudden increases (actual beats) - compare to last frame
        const intensityDelta = rawIntensity - lastIntensity;
        lastIntensity = rawIntensity;
        
        // Peak detection with lower threshold for more sensitivity
        const now = Date.now();
        if ((rawIntensity > 0.2 || intensityDelta > 0.08) && (now - lastPeakTime) > 80) {
          lastPeakTime = now;
          peakHoldTime = now + 120; // Hold peak for 120ms for stronger visual
        }
        
        // Use peak or current intensity
        const isPeakHolding = now < peakHoldTime;
        const displayIntensity = isPeakHolding ? Math.max(rawIntensity, 0.85) : rawIntensity;
        
        // Ultra-extreme amplification - multiply by 4 then apply exponential curve
        const amplified = Math.min(Math.pow(displayIntensity * 4, 1.8), 1);
        
        setBeatIntensity(amplified);
        
        animationFrameId = requestAnimationFrame(detectBeat);
      } catch (error) {
        // Silently handle CORS/CSP errors to prevent console spam
        console.warn('Beat detection error (CORS/CSP), stopping beat sync');
        setBeatIntensity(0.5); // Set to medium intensity as fallback
        // Don't continue the animation loop if there's an error
      }
    };

    detectBeat();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [gradientMode, isPlaying, isSpotifyTrack]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressChange = async (e) => {
    const newTime = parseFloat(e.target.value);
    setProgress(newTime);
    
    if (isSpotifyTrack && spotifyPlayer.isReady) {
      // Seek in Spotify player
      await spotifyPlayer.seek(Math.floor(newTime * 1000)); // Convert to ms
    } else if (audioRef.current) {
      // Seek in HTML5 audio
      audioRef.current.currentTime = newTime;
    }
    
    if (onSeek) {
      onSeek(newTime);
    }
  };

  // Calculate gradient opacity based on mode and beat
  const getGradientStyle = () => {
    if (gradientMode === 'off') return { display: 'none' };
    
    // If beat sync mode but Spotify track (no beat detection), use full intensity
    let baseOpacity;
    if (gradientMode === 'on' || (gradientMode === 'sync' && isSpotifyTrack)) {
      baseOpacity = 1;
    } else {
      baseOpacity = beatIntensity * 2; // Double intensity for better visibility
    }
    
    const intensity = Math.max(0.5, baseOpacity); // Minimum 50% opacity for better visibility
    
    return {
      background: `linear-gradient(90deg, 
        rgba(255, 0, 255, ${intensity}), 
        rgba(0, 255, 255, ${intensity}), 
        rgba(255, 0, 255, ${intensity}))`,
      backgroundSize: '200% 100%',
      animation: gradientMode === 'on' ? 'gradientSlide 3s ease infinite' : 'none',
      height: '6px',
      width: '100%',
      boxShadow: `0 0 20px rgba(255, 0, 255, ${intensity * 0.5}), 0 0 40px rgba(0, 255, 255, ${intensity * 0.5})`,
      transition: gradientMode === 'sync' && !isSpotifyTrack ? 'background 0.05s ease, box-shadow 0.05s ease' : 'none'
    };
  };

  if (!currentTrack) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(135deg, rgba(10, 10, 20, 0.98), rgba(20, 10, 30, 0.98))',
      padding: isMinimized ? '0' : '1rem',
      zIndex: 999998,
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease'
    }}>
      {/* Animated Gradient Border at Top - Absolute position */}
      <div style={{
        ...getGradientStyle(),
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999999
      }} />
      
      {/* Hidden audio element for non-Spotify tracks */}
      <audio ref={audioRef} />

      {/* Hide/Show Tab - Left Side */}
      <div 
        onClick={() => setIsMinimized(!isMinimized)}
        style={{
          position: 'absolute',
          top: '-24px',
          left: '2rem',
          background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3))',
          border: '2px solid rgba(0, 255, 255, 0.5)',
          borderBottom: 'none',
          borderRadius: '6px 6px 0 0',
          padding: '0.25rem 0.75rem',
          cursor: 'pointer',
          fontSize: '0.7rem',
          fontWeight: '600',
          color: '#00ffff',
          transition: 'all 0.2s',
          zIndex: 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.5), rgba(255, 0, 255, 0.5))';
          e.currentTarget.style.top = '-26px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3))';
          e.currentTarget.style.top = '-24px';
        }}
      >
        {isMinimized ? '▲ Show' : '▼ Hide'}
      </div>

      {/* Minimized State - Nothing visible */}
      {isMinimized ? null : (
        /* Full Player State */
        <>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr 2fr 1fr',
        gap: '1rem',
        alignItems: 'center'
      }}>
        {/* Left: Current Track Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {currentTrack.artwork && (
            <img 
              src={currentTrack.artwork} 
              alt={currentTrack.title}
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentTrack.title}
            </div>
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentTrack.artist}
            </div>
            <div style={{
              color: 'rgba(0, 255, 255, 0.7)',
              fontSize: '0.75rem',
              marginTop: '0.25rem'
            }}>
              {currentTrack.source === 'spotify' 
                ? '🎵 Spotify Preview' 
                : currentTrack.source === 'discovery' 
                  ? '🔍 Discovery Preview (30s)' 
                  : currentTrack.source === 'local'
                    ? '📁 Local Track (Full)'
                    : '🎧 BEATSEEK'} • {playbackMode}
            </div>
          </div>
        </div>

        {/* Center: Playback Controls */}
        <div style={{ textAlign: 'center' }}>
          {/* Control Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <button
              onClick={onPrevious}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = '#00ffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
              title="Previous track"
            >
              ⏮️
            </button>

            <button
              onClick={onPlayPause}
              style={{
                background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                border: 'none',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: 'white',
                fontSize: '1.2rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </button>

            <button
              onClick={onNext}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = '#00ffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
              title="Next track"
            >
              ⏭️
            </button>
          </div>

          {/* Progress Bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', minWidth: '35px' }}>
              {formatTime(progress)}
            </span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={progress}
              onChange={handleProgressChange}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                background: `linear-gradient(to right, #00ffff 0%, #00ffff ${(progress / duration) * 100}%, rgba(255, 255, 255, 0.2) ${(progress / duration) * 100}%, rgba(255, 255, 255, 0.2) 100%)`,
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', minWidth: '35px' }}>
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Right: Volume, Playlist Info & Exit */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem' }}>
          {playlist && (
            <div style={{ textAlign: 'right', marginRight: '1rem' }}>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                {playlist.name}
              </div>
              <div style={{ color: 'rgba(0, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                {playlist.currentIndex + 1} / {playlist.totalTracks}
              </div>
            </div>
          )}
          
          {/* Volume Control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={async () => {
                const newMutedState = !isMuted;
                setIsMuted(newMutedState);
                
                if (audioRef.current) {
                  audioRef.current.muted = newMutedState;
                  if (!newMutedState) {
                    audioRef.current.volume = volume;
                  }
                }
                
                // Also mute Spotify if it's a Spotify track (use player instance directly)
                if (isSpotifyTrack && spotifyPlayer.player) {
                  try {
                    await spotifyPlayer.player.setVolume(newMutedState ? 0 : volume);
                  } catch (error) {
                    console.error('Error setting Spotify volume:', error);
                  }
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.25rem',
                color: isMuted ? '#ff4444' : 'white'
              }}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🔊'}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={async (e) => {
                const newVolume = parseFloat(e.target.value);
                setVolume(newVolume);
                setIsMuted(newVolume === 0);
                
                if (audioRef.current) {
                  audioRef.current.volume = newVolume;
                  audioRef.current.muted = newVolume === 0;
                }
                
                // Also update Spotify volume (use player instance directly)
                if (isSpotifyTrack && spotifyPlayer.player) {
                  try {
                    await spotifyPlayer.player.setVolume(newVolume);
                  } catch (error) {
                    console.error('Error setting Spotify volume:', error);
                  }
                }
              }}
              className="playlist-volume-slider"
              style={{
                width: '80px',
                height: '6px',
                WebkitAppearance: 'none',
                appearance: 'none',
                borderRadius: '3px',
                background: `linear-gradient(to right, #00ffff 0%, #00ffff ${(isMuted ? 0 : volume) * 100}%, rgba(255, 255, 255, 0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(255, 255, 255, 0.2) 100%)`,
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Gradient Mode Toggle Button */}
          <button
            onClick={() => {
              const modes = ['off', 'on', 'sync'];
              const currentIndex = modes.indexOf(gradientMode);
              const nextMode = modes[(currentIndex + 1) % modes.length];
              setGradientMode(nextMode);
              
              // Show warning if trying to use beat sync with Spotify
              if (nextMode === 'sync' && isSpotifyTrack) {
                console.warn('⚠️ Beat sync only works with Discovery Zone tracks. Spotify tracks use SDK playback without audio element access. Using gradient ON mode instead.');
              }
            }}
            style={{
              padding: '0.3rem 0.6rem',
              background: gradientMode === 'off' 
                ? 'rgba(100, 100, 100, 0.2)' 
                : gradientMode === 'on' 
                  ? 'rgba(255, 0, 255, 0.2)' 
                  : 'rgba(0, 255, 255, 0.2)',
              border: gradientMode === 'off' 
                ? '1px solid rgba(150, 150, 150, 0.4)' 
                : gradientMode === 'on' 
                  ? '1px solid rgba(255, 0, 255, 0.6)' 
                  : '1px solid rgba(0, 255, 255, 0.6)',
              borderRadius: '4px',
              color: gradientMode === 'off' ? '#aaa' : gradientMode === 'on' ? '#ff00ff' : '#00ffff',
              fontSize: '0.7rem',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              marginRight: '0.25rem'
            }}
            title={`Gradient: ${gradientMode === 'off' ? 'Off' : gradientMode === 'on' ? 'On' : 'Beat Sync'} (Click to toggle)`}
          >
            {gradientMode === 'off' ? '⚫ Gradient' : gradientMode === 'on' ? '🌈 Gradient' : '🎵 Beat Sync'}
          </button>

          {/* Back to Playlist Button */}
          <button
            onClick={() => {
              if (onBackToPlaylist) {
                onBackToPlaylist();
              } else {
                console.error('onBackToPlaylist function not provided');
              }
            }}
            style={{
              padding: '0.3rem 0.6rem',
              background: 'rgba(0, 255, 255, 0.2)',
              border: '1px solid rgba(0, 255, 255, 0.5)',
              borderRadius: '4px',
              color: '#00ffff',
              fontSize: '0.7rem',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              marginRight: '0.25rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.3)';
              e.currentTarget.style.borderColor = '#00ffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
            }}
            title="Back to playlists"
          >
            ↩️ Playlists
          </button>

          {/* Exit Playlist Button */}
          <button
            onClick={onExit}
            style={{
              padding: '0.3rem 0.6rem',
              background: 'rgba(255, 0, 0, 0.2)',
              border: '1px solid rgba(255, 0, 0, 0.5)',
              borderRadius: '4px',
              color: '#ff4444',
              fontSize: '0.7rem',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 0, 0.3)';
              e.currentTarget.style.borderColor = '#ff4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 0, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.5)';
            }}
            title="Exit playlist"
          >
            ✕ Exit
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default PlaylistPlayer;
